import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  ArchivePromptVariantResponseSchema,
  ComparePromptParamsSchema,
  ComparePromptResponseSchema,
  CreatePromptVariantBodySchema,
  CreatePromptVariantResponseSchema,
  ForkPromptVariantBodySchema,
  ForkPromptVariantResponseSchema,
  ListPromptVariantsResponseSchema,
  ListPromptVariantVersionsResponseSchema,
  PromotePromptVariantBodySchema,
  PromotePromptVariantResponseSchema,
  PromptGraphResponseSchema,
  RenamePromptVariantBodySchema,
  RenamePromptVariantResponseSchema,
  SavePromptVariantVersionBodySchema,
  SavePromptVariantVersionResponseSchema
} from "../../public/v1/schemas/prompt-variant.schemas";

/**
 * ZPromptVariants — internal contract for branch-based prompt versioning
 * (Prompt Lab phase 1). Same shapes as the public v1 surface — the schemas are
 * imported from `public/v1/schemas/prompt-variant.schemas`, the direction
 * tracks/tickets/analytics already established — wrapped in the internal
 * success envelope.
 *
 * All routes are nested under /api/agents/:agentId/prompt-variants.
 * `:variantRef` is a variant id, a variant name, or "main" (case-insensitive).
 */
export const ZPromptVariants = {
  /** GET /api/agents/:agentId/prompt-variants — active variants (Main first) */
  List: {
    method: "GET" as const,
    path: "/api/agents/:agentId/prompt-variants",
    PathVars: z.object({ agentId: z.string() }),
    Query: z.object({
      includeArchived: z
        .enum(["true", "false"])
        .transform((v) => v === "true")
        .optional()
    }),
    Response: ApiSuccessSchema(ListPromptVariantsResponseSchema)
  },

  /** POST /api/agents/:agentId/prompt-variants — fork a new variant (default source: Main tip) */
  Create: {
    method: "POST" as const,
    path: "/api/agents/:agentId/prompt-variants",
    PathVars: z.object({ agentId: z.string() }),
    Body: CreatePromptVariantBodySchema,
    Response: ApiSuccessSchema(CreatePromptVariantResponseSchema)
  },

  /** PATCH /api/agents/:agentId/prompt-variants/:variantRef — rename */
  Rename: {
    method: "PATCH" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Body: RenamePromptVariantBodySchema,
    Response: ApiSuccessSchema(RenamePromptVariantResponseSchema)
  },

  /** DELETE /api/agents/:agentId/prompt-variants/:variantRef — archive (nothing is deleted) */
  Archive: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Response: ApiSuccessSchema(ArchivePromptVariantResponseSchema)
  },

  /** POST /api/agents/:agentId/prompt-variants/:variantRef/fork — fork from this variant's tip */
  Fork: {
    method: "POST" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef/fork",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Body: ForkPromptVariantBodySchema,
    Response: ApiSuccessSchema(ForkPromptVariantResponseSchema)
  },

  /** POST /api/agents/:agentId/prompt-variants/:variantRef/promote — copy tip into a NEW Main version */
  Promote: {
    method: "POST" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef/promote",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Body: PromotePromptVariantBodySchema,
    Response: ApiSuccessSchema(PromotePromptVariantResponseSchema)
  },

  /** POST /api/agents/:agentId/prompt-variants/:variantRef/versions — save (append to tip) */
  SaveVersion: {
    method: "POST" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef/versions",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Body: SavePromptVariantVersionBodySchema,
    Response: ApiSuccessSchema(SavePromptVariantVersionResponseSchema)
  },

  /** GET /api/agents/:agentId/prompt-variants/:variantRef/versions — history, ascending ordinal */
  VersionList: {
    method: "GET" as const,
    path: "/api/agents/:agentId/prompt-variants/:variantRef/versions",
    PathVars: z.object({ agentId: z.string(), variantRef: z.string() }),
    Response: ApiSuccessSchema(ListPromptVariantVersionsResponseSchema)
  },

  /** GET /api/agents/:agentId/prompt-graph — nodes (versions) + edges (forks, promotes) */
  Graph: {
    method: "GET" as const,
    path: "/api/agents/:agentId/prompt-graph",
    PathVars: z.object({ agentId: z.string() }),
    Response: ApiSuccessSchema(PromptGraphResponseSchema)
  },

  /** GET /api/agents/:agentId/prompt-compare?a=&b= — line diff between two refs */
  Compare: {
    method: "GET" as const,
    path: "/api/agents/:agentId/prompt-compare",
    PathVars: z.object({ agentId: z.string() }),
    Query: ComparePromptParamsSchema,
    Response: ApiSuccessSchema(ComparePromptResponseSchema)
  }
} as const;
